import React, { useState } from 'react';
import { Modal, TextInput, Text, Stack, Group, Paper, Title, rem, Loader } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import Button from './Button';
import FAIcon from './FAIcon';

interface WishlistModalProps {
    opened: boolean;
    onClose: () => void;
    service?: string;
}

const WishlistModal: React.FC<WishlistModalProps> = ({ opened, onClose, service }) => {
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [alreadyExists, setAlreadyExists] = useState(false);

    const form = useForm({
        initialValues: {
            email: '',
        },
        validate: {
            email: (value) => {
                if (!value) return 'Email is required';
                if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
                    return 'Please enter a valid email address';
                }
                return null;
            },
        },
    });

    const handleSubmit = async (values: { email: string }) => {
        try {
            setLoading(true);
            const response = await fetch('/api/wishlist', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: values.email,
                    service: service || 'general'
                }),
            });
            console.log('response', response);
            let data;
            try {
                data = await response.json();
                console.log('data', data);
            } catch (e) {
                console.error('Failed to parse JSON:', e);
            }

            if (data?.isExisting) {
                setAlreadyExists(true);
                return;
            } else if (data?.success) {
                setSuccess(true);
                setTimeout(() => {
                    setSuccess(false);
                    onClose();
                    form.reset();
                }, 5000);
            } else {
                throw new Error(data?.error || 'Failed to add to wishlist');
            }
        } catch (error) {
            notifications.show({
                title: 'Error',
                message: error instanceof Error ? error.message : 'Failed to add to wishlist',
                color: 'red',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            centered
            size="md"
            padding="xl"
            radius="lg"
            withCloseButton
            styles={{
                content: {
                    background: 'linear-gradient(135deg, #f8fafc 0%, #e0e7ef 100%)',
                },
                title: {
                    fontSize: rem(24),
                    fontWeight: 700,
                },
                header: {
                    marginBottom: rem(20),
                },
            }}
        >
            <Paper radius="xl" p="xl" withBorder shadow="md" style={{ border: '1.5px solid #e0e7ef', boxShadow: '0 8px 32px 0 rgba(60, 80, 180, 0.08)' }}>
                <Stack gap="lg">
                    <div style={{ textAlign: 'center', marginBottom: rem(8) }}>
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(135deg, #3b82f6 60%, #6366f1 100%)',
                            borderRadius: '50%',
                            width: 64,
                            height: 64,
                            margin: '0 auto',
                            marginBottom: rem(16),
                            boxShadow: '0 2px 8px 0 rgba(60, 80, 180, 0.10)'
                        }}>
                            <FAIcon name="envelope" size={32} style={{ color: '#fff' }} />
                        </div>
                        <Title order={2} mb="xs" style={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
                            Join Our Wishlist
                        </Title>
                        <Text size="md" c="dimmed" mb="xl" style={{ maxWidth: 340, margin: '0 auto' }}>
                            Be the first to know about our latest updates and exclusive offers.
                        </Text>
                    </div>
                    {success ? (
                        <Stack align="center" gap="md" py="xl">
                            <FAIcon name="check-circle" size={48} style={{ color: '#22c55e' }} />
                            <Title order={3} style={{ color: '#22c55e', fontWeight: 700 }}>Success!</Title>
                            <Text c="dimmed" size="md" ta="center">
                                You have been added to our wishlist. Thank you!
                            </Text>
                        </Stack>
                    ) : (
                        <form onSubmit={form.onSubmit(handleSubmit)}>
                            <Stack gap="md">
                                {alreadyExists && (
                                    <Stack align="center" gap="xs" py="sm">
                                        <FAIcon name="exclamation-circle" size={32} style={{ color: '#f59e42' }} />
                                        <Text c="orange" size="md" ta="center" fw={600}>
                                            This email is already on our wishlist!
                                        </Text>
                                    </Stack>
                                )}
                                <TextInput
                                    required
                                    label={<span style={{ fontWeight: 600, fontSize: rem(15) }}>Email Address</span>}
                                    placeholder="your@email.com"
                                    size="lg"
                                    radius="xl"
                                    leftSection={<FAIcon name="envelope" size={18} style={{ color: '#3b82f6' }} />}
                                    error={form.errors.email}
                                    {...form.getInputProps('email')}
                                    styles={{
                                        input: {
                                            background: '#f4f7fb',
                                            border: alreadyExists ? '1.5px solid #f59e42' : '1.5px solid #e0e7ef',
                                            fontSize: rem(16),
                                            paddingLeft: rem(44),
                                            height: rem(48),
                                            borderRadius: rem(24),
                                            transition: 'border-color 0.2s',
                                            '&:focus': {
                                                borderColor: alreadyExists ? '#f59e42' : '#3b82f6',
                                                boxShadow: alreadyExists
                                                    ? '0 0 0 2px #f59e4233'
                                                    : '0 0 0 2px #3b82f633',
                                            },
                                        },
                                        label: {
                                            marginBottom: rem(8),
                                        },
                                    }}
                                    autoComplete="email"
                                    autoFocus
                                    description={<Text size="xs" c="dimmed">We&lsquo;ll never share your email with anyone else.</Text>}
                                    onChange={e => {
                                        form.setFieldValue('email', e.currentTarget.value);
                                        if (alreadyExists) setAlreadyExists(false);
                                    }}
                                />
                                <Group justify="flex-end" mt="xl">
                                    <Button
                                        variant="outline"
                                        onClick={onClose}
                                        size="medium"
                                        component='button'
                                        transition={false}
                                        style={{
                                            minWidth: rem(120),
                                            borderRadius: 24,
                                            fontWeight: 700,
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        loading={loading ? true : undefined}
                                        size="medium"
                                        component='button'
                                        transition={false}
                                        style={{
                                            minWidth: rem(120),
                                            borderRadius: 24,
                                            fontWeight: 700,
                                            background: 'linear-gradient(90deg, #3b82f6 0%, #6366f1 100%)',
                                            color: '#fff',
                                            boxShadow: '0 2px 8px 0 rgba(60, 80, 180, 0.10)',
                                        }}
                                        expandOnHover
                                    >
                                        {
                                            loading ? <span> <Loader size={'md'} /></span> : 'Join Wishlist'
                                        }
                                    </Button>
                                </Group>
                            </Stack>
                        </form>
                    )}
                </Stack>
            </Paper>
        </Modal>
    );
};

export default WishlistModal; 